import React, { useMemo } from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { StarFilled, StarHalfFilled } from '@withjoy/joykit/icons';

import { useTranslation } from '@shared/core';
import { Skeleton, SkeletonText } from '@shared/components/Skeleton';
import { formatUserRatingNumber, useReviewCountFormatter } from '@shared/utils/accommodations/AccommodationTiles';

const DEFAULT_MIN_REVIEW_COUNT = 10;

type UserRatingProps = Readonly<{
  rating: number | null | undefined;
  reviewCount: number | null | undefined;
  minReviewCount?: number;
}>;

const StarRating = ({ rating }: { rating: number }) => {
  const isHalfRating = rating % 1 === 0.5;
  return (
    <Flex columnGap={pxToRem(2)}>
      {Array.from({ length: Math.floor(rating) }, (_, index) => (
        <StarFilled
          size={16}
          key={index}
        />
      ))}
      {isHalfRating && <StarHalfFilled size={16} />}
    </Flex>
  );
};

StarRating.Skeleton = () => {
  return (
    <Flex columnGap={pxToRem(4)}>
      <Skeleton
        width={14}
        height={13}
      />
      <Skeleton
        width={14}
        height={13}
      />
      <Skeleton
        width={14}
        height={13}
      />
      <Skeleton
        width={14}
        height={13}
      />
      <Skeleton
        width={14}
        height={13}
      />
    </Flex>
  );
};

const UserRating = ({ rating, reviewCount, minReviewCount = DEFAULT_MIN_REVIEW_COUNT }: UserRatingProps) => {
  const { t } = useTranslation('accommodationsConsole');
  const formatReviewCount = useReviewCountFormatter();

  const label = useMemo(() => {
    if (!rating) {
      return null;
    }
    const categoryTrans = t('listingsPanel', 'userRatings', 'category');
    switch (true) {
      case rating >= 9:
        return categoryTrans.wonderful();
      case rating >= 8.5:
        return categoryTrans.fabulous();
      case rating >= 8:
        return categoryTrans.veryGood();
      case rating >= 7:
        return categoryTrans.good();
      case rating >= 6:
        return categoryTrans.pleasant();
      default:
        return null;
    }
  }, [rating, t]);

  const reviewCountText = useMemo(() => {
    if (!reviewCount) {
      return null;
    }
    return t('listingsPanel', 'userRatings', 'reviewCounts')?.({ reviewCount: formatReviewCount(reviewCount) });
  }, [formatReviewCount, reviewCount, t]);

  if (!rating || !reviewCount || reviewCount < minReviewCount) {
    return null;
  }

  return (
    <Flex
      alignItems="center"
      flexDirection="row"
      columnGap={{ _: 3, sm2: 2 }}
    >
      {typeof rating === 'number' && rating > 0 && (
        <Flex
          color="white"
          flexShrink={0}
          alignItems="center"
          width={pxToRem(34)}
          height={pxToRem(34)}
          justifyContent="center"
          borderRadius={pxToRem(8)}
          backgroundColor="mono14"
        >
          <TextV2
            fontWeight={600}
            typographyVariant="label2"
          >
            {formatUserRatingNumber(rating)}
          </TextV2>
        </Flex>
      )}
      {(label || !!reviewCountText) && (
        <Flex
          rowGap={1}
          columnGap={2}
          alignItems={{ _: 'center', sm2: 'none' }}
          flexDirection={{ _: 'row', sm2: 'column' }}
        >
          {label && (
            <TextV2
              whiteSpace="nowrap"
              typographyVariant="button1"
            >
              {label}
            </TextV2>
          )}
          {!!reviewCountText && (
            <>
              <TextV2
                color="mono10"
                whiteSpace="nowrap"
                typographyVariant="label2"
                display={{ _: 'inline', sm2: 'none' }}
              >
                ({reviewCountText})
              </TextV2>
              <TextV2
                color="mono10"
                whiteSpace="nowrap"
                typographyVariant="label2"
                display={{ _: 'none', sm2: 'inline' }}
              >
                {reviewCountText}
              </TextV2>
            </>
          )}
        </Flex>
      )}
    </Flex>
  );
};

UserRating.Skeleton = () => {
  return (
    <Flex
      alignItems="center"
      flexDirection="row"
      columnGap={{ _: 3, sm2: 2 }}
    >
      <Skeleton
        height={pxToRem(34)}
        width={pxToRem(34)}
      />
      <Flex
        flexDirection={{ _: 'row', sm2: 'column' }}
        alignItems={{ _: 'center', sm2: 'none' }}
        columnGap={2}
        rowGap={pxToRem(10)}
      >
        <SkeletonText
          rows={1}
          width={75}
          skeletonHeight={20}
        />
        <SkeletonText
          rows={1}
          width={75}
          skeletonHeight={14}
        />
      </Flex>
    </Flex>
  );
};

export { UserRating, StarRating };
